import React, { useRef, useState } from 'react';
import { Modal } from 'react-bootstrap-v5';
import { AnimatePresence, motion } from 'framer-motion';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import ScreenshotAnnotator, { AnnotatorHandle } from './ScreenshotAnnotator';
import { CATEGORY_OPTIONS, STATUS_OPTIONS, formatDate, optColor, optLabel } from './constants';
import { useMySupportRequests, useSupportRequestCreate, useSupportRequestDetail } from './service';

type Props = {
  show: boolean;
  onClose: () => void;
  screenshot: string | null;
  pageUrl: string;
};

type Tab = 'new' | 'mine';

const feedbackSchema = Yup.object().shape({
  title: Yup.string().trim().min(3, 'Başlık en az 3 karakter olmalı.').max(150, 'Başlık çok uzun.').required('Lütfen bir başlık girin.'),
  description: Yup.string().max(2000, 'Açıklama çok uzun.'),
  category: Yup.string().required(),
});

// --- Taleplerim: tek kayıt (genişletince zaman çizelgesini getirir) --------
const MyRequestCard: React.FC<{ request: any }> = ({ request }) => {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useSupportRequestDetail(open ? request.id : undefined);
  const events = data?.events || [];

  return (
    <div className="border border-gray-300 rounded mb-3 overflow-hidden">
      <div
        className="d-flex align-items-center justify-content-between p-3 cursor-pointer bg-light-primary"
        role="button"
        onClick={() => setOpen((o) => !o)}
      >
        <div className="d-flex flex-column">
          <span className="fw-bold text-gray-800">{request.title}</span>
          <span className="text-muted fs-8">
            #{request.id} • {formatDate(request.created_at)}
          </span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className={`badge badge-light-${optColor(STATUS_OPTIONS, request.status)}`}>{optLabel(STATUS_OPTIONS, request.status)}</span>
          <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'} text-muted`} />
        </div>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
            <div className="p-3">
              {request.description && <p className="text-gray-700 fs-7 mb-3">{request.description}</p>}
              {request.screenshot_url && (
                <a href={request.screenshot_url} target="_blank" rel="noreferrer" className="d-inline-block mb-3">
                  <img src={request.screenshot_url} alt="ekran görüntüsü" style={{ maxHeight: 120, borderRadius: 6, border: '1px solid #eee' }} />
                </a>
              )}

              <div className="fw-bold text-muted fs-8 text-uppercase mb-2">Durum Geçmişi</div>
              {isLoading && <div className="text-muted fs-8">Yükleniyor...</div>}
              {!isLoading && events.length === 0 && <div className="text-muted fs-8">Henüz güncelleme yok.</div>}
              <div className="timeline timeline-border-dashed">
                {events.map((ev: any) => (
                  <div key={ev.id} className="d-flex mb-3">
                    <div className="me-3">
                      <span className={`bullet bullet-dot bg-${ev.event_type === 'status_change' ? optColor(STATUS_OPTIONS, ev.to_status) : 'secondary'} h-15px w-15px`} />
                    </div>
                    <div className="flex-grow-1">
                      <div className="fs-8 text-gray-800">
                        {ev.event_type === 'created' && 'Talep oluşturuldu'}
                        {ev.event_type === 'status_change' && (
                          <>
                            Durum <span className="fw-bold">{optLabel(STATUS_OPTIONS, ev.to_status)}</span> olarak güncellendi
                          </>
                        )}
                        {ev.event_type === 'note' && 'Not eklendi'}
                      </div>
                      {ev.message && <div className="fs-8 text-gray-600 fst-italic">"{ev.message}"</div>}
                      <div className="fs-9 text-muted">{formatDate(ev.created_at)}{ev.actor_name ? ` • ${ev.actor_name}` : ''}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function SupportFeedbackModal({ show, onClose, screenshot, pageUrl }: Props) {
  const [tab, setTab] = useState<Tab>('new');
  const [includeShot, setIncludeShot] = useState(true);
  const annotatorRef = useRef<AnnotatorHandle>(null);

  const { data: myData, refetch: refetchMine, isLoading: mineLoading } = useMySupportRequests(show);
  const myRequests = myData?.results || [];

  const onSuccess = () => {
    toast.success('Destek talebiniz oluşturuldu. Teşekkürler!');
    refetchMine();
    setTab('mine');
  };
  const onError = (e: any) => toast.error(e?.response?.data?.message || 'Talep oluşturulurken bir hata oluştu.');
  const { mutate, isLoading } = useSupportRequestCreate(onSuccess, onError);

  const buildAndSubmit = async (values: any, reset: () => void) => {
    const fd = new FormData();
    fd.append('title', values.title);
    fd.append('description', values.description || '');
    fd.append('category', values.category);
    fd.append('page_url', pageUrl);
    fd.append(
      'meta',
      JSON.stringify({
        userAgent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        screen: `${window.screen.width}x${window.screen.height}`,
      }),
    );

    if (screenshot && includeShot && annotatorRef.current) {
      const blob = await annotatorRef.current.getCompositeBlob();
      if (blob) fd.append('images', blob, `ekran-goruntusu-${Date.now()}.jpg`);
    }
    mutate(fd, { onSuccess: () => reset() });
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered scrollable>
      <div className="modal-content border-0">
        <div className="modal-header py-4" style={{ background: 'linear-gradient(135deg, #1f2b37 0%, #2c3e50 100%)' }}>
          <h5 className="modal-title text-white d-flex align-items-center">
            <i className="bi bi-life-preserver me-2 fs-3" /> Destek & Geri Bildirim
          </h5>
          <button type="button" className="btn btn-icon btn-sm btn-active-color-white" onClick={onClose} aria-label="Kapat">
            <i className="bi bi-x-lg text-white" />
          </button>
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs nav-line-tabs px-5 pt-3">
          <li className="nav-item">
            <button className={`nav-link ${tab === 'new' ? 'active' : ''}`} onClick={() => setTab('new')} type="button">
              <i className="bi bi-plus-circle me-1" /> Yeni Talep
            </button>
          </li>
          <li className="nav-item">
            <button className={`nav-link ${tab === 'mine' ? 'active' : ''}`} onClick={() => setTab('mine')} type="button">
              <i className="bi bi-list-check me-1" /> Taleplerim {myRequests.length > 0 && <span className="badge badge-circle badge-light-primary ms-1">{myRequests.length}</span>}
            </button>
          </li>
        </ul>

        <div className="modal-body">
          <AnimatePresence mode="wait">
            {tab === 'new' ? (
              <motion.div key="new" initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 12 }} transition={{ duration: 0.18 }}>
                <Formik
                  initialValues={{ title: '', description: '', category: 'bug' }}
                  validationSchema={feedbackSchema}
                  onSubmit={(values, { resetForm }) => buildAndSubmit(values, resetForm)}
                >
                  {({ values, errors, touched, handleChange, handleSubmit, setFieldValue }) => (
                    <form onSubmit={handleSubmit}>
                      {/* Kategori seçimi (chip'ler) */}
                      <label className="form-label fw-semibold">Kategori</label>
                      <div className="d-flex flex-wrap gap-2 mb-4">
                        {CATEGORY_OPTIONS.map((c) => (
                          <button
                            type="button"
                            key={c.value}
                            className={`btn btn-sm ${values.category === c.value ? `btn-${c.color}` : `btn-light-${c.color}`}`}
                            onClick={() => setFieldValue('category', c.value)}
                          >
                            <i className={`bi ${c.icon} me-1`} />
                            {c.label}
                          </button>
                        ))}
                      </div>

                      <div className="mb-4">
                        <label className="form-label required fw-semibold">Başlık</label>
                        <input
                          name="title"
                          className={`form-control form-control-solid ${touched.title && errors.title ? 'is-invalid' : ''}`}
                          placeholder="Sorunu kısaca özetleyin"
                          value={values.title}
                          onChange={handleChange}
                        />
                        {touched.title && errors.title && <div className="text-danger fs-8 mt-1">{errors.title}</div>}
                      </div>

                      <div className="mb-4">
                        <label className="form-label fw-semibold">Açıklama</label>
                        <textarea
                          name="description"
                          className="form-control form-control-solid"
                          rows={3}
                          placeholder="Ne yapmaya çalışıyordunuz? Ne bekliyordunuz, ne oldu?"
                          value={values.description}
                          onChange={handleChange}
                        />
                        {touched.description && errors.description && <div className="text-danger fs-8 mt-1">{errors.description}</div>}
                      </div>

                      {/* Ekran görüntüsü + anotasyon */}
                      {screenshot && (
                        <div className="mb-4">
                          <div className="form-check form-switch mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="includeShot"
                              checked={includeShot}
                              onChange={(e) => setIncludeShot(e.target.checked)}
                            />
                            <label className="form-check-label fw-semibold" htmlFor="includeShot">
                              Ekran görüntüsünü ekle
                            </label>
                          </div>
                          {includeShot && <ScreenshotAnnotator ref={annotatorRef} imageDataUrl={screenshot} />}
                        </div>
                      )}

                      <div className="d-flex justify-content-end gap-2 pt-2">
                        <button type="button" className="btn btn-light" onClick={onClose}>
                          Vazgeç
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={isLoading}>
                          <i className="bi bi-send me-1" />
                          Gönder
                          {isLoading && <span className="spinner-border spinner-border-sm align-middle ms-2" />}
                        </button>
                      </div>
                    </form>
                  )}
                </Formik>
              </motion.div>
            ) : (
              <motion.div key="mine" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.18 }}>
                {mineLoading && <div className="text-center text-muted py-6">Yükleniyor...</div>}
                {!mineLoading && myRequests.length === 0 && (
                  <div className="text-center text-muted py-8">
                    <i className="bi bi-inbox fs-2x d-block mb-2" />
                    Henüz bir talebiniz yok.
                  </div>
                )}
                {myRequests.map((r: any) => (
                  <MyRequestCard key={r.id} request={r} />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </Modal>
  );
}
